import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import { Modal, Button, FormGroup, FormControl, ControlLabel } from 'react-bootstrap';

// Style
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './PayoutMegasoftManagement.css';

import CurrencyConverter from '../../CurrencyConverter';
import Link from '../../Link';
import CustomPagination from '../../CustomPagination';
import CommonTable from '../../CommonTable/CommonTable';
import Loader from '../../Loader';

// Translation
import messages from '../../../locale/messages';

class PayoutMegasoftManagement extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      payouts: [],
      loading: false,
      error: null,
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalCount: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: 10
      },
      filters: {
        method: '',
        isAmountTransfer: '',
        startDate: '',
        endDate: ''
      },
      // Modal state for completing transactions
      showModal: false,
      selectedPayoutId: null,
      bankReference: '',
      notes: '',
      modalError: '',
      submitting: false
    }
    this.paginationData = this.paginationData.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.fetchPayouts = this.fetchPayouts.bind(this);
    this.completeTransaction = this.completeTransaction.bind(this);
    this.openCompleteModal = this.openCompleteModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleSubmitComplete = this.handleSubmitComplete.bind(this);
  }

  componentDidMount() {
    this.fetchPayouts();
  }

  componentDidUpdate(prevProps) {
    const { currentPage, method, isAmountTransfer, startDate, endDate } = this.props;
    const prevPropsState = {
      currentPage: prevProps.currentPage,
      method: prevProps.method,
      isAmountTransfer: prevProps.isAmountTransfer,
      startDate: prevProps.startDate,
      endDate: prevProps.endDate
    };

    const currentPropsState = { currentPage, method, isAmountTransfer, startDate, endDate };

    if (JSON.stringify(prevPropsState) !== JSON.stringify(currentPropsState)) {
      this.fetchPayouts();
    }
  }

  async fetchPayouts() {
    const { currentPage, method, isAmountTransfer, startDate, endDate } = this.props;

    this.setState({ loading: true, error: null });

    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10
      });

      if (method) params.append('method', method);
      if (isAmountTransfer !== '') params.append('isAmountTransfer', isAmountTransfer);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await fetch(`/web-api/payout-megasoft?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        this.setState({
          payouts: result.data.payouts,
          pagination: result.data.pagination,
          loading: false
        });
      } else {
        this.setState({
          error: result.error,
          loading: false
        });
      }
    } catch (error) {
      this.setState({
        error: error.message,
        loading: false
      });
    }
  }

  // Open modal to complete a transaction
  openCompleteModal(payoutId) {
    this.setState({
      showModal: true,
      selectedPayoutId: payoutId,
      bankReference: '',
      notes: '',
      modalError: '',
      submitting: false
    });
  }

  // Close the modal
  closeModal() {
    this.setState({
      showModal: false,
      selectedPayoutId: null,
      bankReference: '',
      notes: '',
      modalError: '',
      submitting: false
    });
  }

  // Handle form submission
  async handleSubmitComplete(e) {
    e.preventDefault();
    
    const { selectedPayoutId, bankReference, notes } = this.state;
    
    // Validate bank reference
    if (!bankReference || bankReference.trim().length < 4) {
      this.setState({ 
        modalError: 'El número de referencia bancaria es obligatorio (mínimo 4 caracteres)' 
      });
      return;
    }

    this.setState({ submitting: true, modalError: '' });

    try {
      const response = await fetch(`/web-api/payout-megasoft/${selectedPayoutId}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bankReference: bankReference.trim(),
          notes: notes.trim() || null
        })
      });

      const result = await response.json();

      if (result.success) {
        this.closeModal();
        this.fetchPayouts();
      } else {
        this.setState({ 
          modalError: result.error || 'Error al completar la transacción',
          submitting: false 
        });
      }
    } catch (error) {
      this.setState({ 
        modalError: 'Error de conexión: ' + error.message,
        submitting: false 
      });
    }
  }

  async completeTransaction(payoutId) {
    // Now opens modal instead of direct completion
    this.openCompleteModal(payoutId);
  }

  // Render status badge with colors
  renderStatusBadge(item) {
    const status = item.status || (item.isAmountTransfer ? 'COMPLETED' : 'PENDING');
    
    const badgeStyles = {
      PENDING: { backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B' },
      COMPLETED: { backgroundColor: '#D1FAE5', color: '#065F46', border: '1px solid #10B981' },
      FAILED: { backgroundColor: '#FEE2E2', color: '#991B1B', border: '1px solid #EF4444' }
    };
    
    const labels = {
      PENDING: 'Pendiente',
      COMPLETED: 'Completado',
      FAILED: 'Fallido'
    };

    const style = {
      ...badgeStyles[status] || badgeStyles.PENDING,
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'inline-block'
    };

    return <span style={style}>{labels[status] || status}</span>;
  }

  paginationData(currentPage) {
    const { setStateVariable } = this.props;
    setStateVariable({ currentPage });
  }

  handleFilterChange = (filterType, value) => {
    const { setStateVariable } = this.props;
    setStateVariable({
      [filterType]: value,
      currentPage: 1
    });
  }

  thead = () => {
    const { formatMessage } = this.props.intl;
    return [
      { data: formatMessage(messages.idLabel) },
      { data: formatMessage(messages.methodLabel) },
      { data: formatMessage(messages.amountLabel) },
      { data: formatMessage(messages.listingTitleLabel) },
      { data: formatMessage(messages.ownerNameLabel) },
      { data: 'Estado' },
      { data: 'Ref. Bancaria' },
      { data: formatMessage(messages.createdDateLabel) },
      { data: formatMessage(messages.detailsLabel) },
      { data: formatMessage(messages.actionsLabel) },
    ]
  };

  tbody = () => {
    const { payouts } = this.state;
    const { formatMessage } = this.props.intl;

    return payouts.map(item => {
      const status = item.status || (item.isAmountTransfer ? 'COMPLETED' : 'PENDING');
      const isCompleted = status === 'COMPLETED' || item.isAmountTransfer;
      
      return {
        id: item.id,
        data: [
          { data: item.id },
          {
            data: <span className={s.methodBadge + ' ' + s[item.method.toLowerCase()]}>
              {item.method}
            </span>
          },
          {
            data: item.method === 'ZELLE'
              ? `$${Number(item.payoutAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : `Bs ${Number(item.payoutAmount).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          },
          {
            data: item.listing ? <a href={"/boats/" + item.listing.id} target='_blank'>
              {item.listing.title}
            </a> : 'N/A'
          },
          {
            data: item.listing?.user?.profile ?
              `${item.listing.user.profile.firstName} ${item.listing.user.profile.lastName}` :
              'N/A'
          },
          {
            data: this.renderStatusBadge(item)
          },
          {
            data: item.bankReference ? (
              <span style={{ fontFamily: 'monospace', fontSize: '13px', color: '#374151' }}>
                {item.bankReference}
              </span>
            ) : (
              <span style={{ color: '#9CA3AF' }}>—</span>
            )
          },
          {
            data: moment(item.createdAt).format('MMM DD, YYYY HH:mm')
          },
          {
            data: <Link to={"/siteadmin/view-payout-megasoft/" + item.id}>
              {formatMessage(messages.detailsLabel)}
            </Link>
          },
          {
            data: !isCompleted ? (
              <button
                className={s.completeButton}
                onClick={() => this.completeTransaction(item.id)}
                style={{
                  backgroundColor: '#0056b3',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Completar Pago
              </button>
            ) : (
              <span style={{ color: '#10B981', fontWeight: '500' }}>
                ✓ Completado
              </span>
            )
          }
        ]
      }
    });
  }

  renderFilters() {
    const { method, isAmountTransfer, startDate, endDate } = this.props;
    const { formatMessage } = this.props.intl;

    return (
      <div className={s.filterSection}>
        <div className={s.filterRow}>
          <div className={s.filterItem}>
            <label>{formatMessage(messages.paymentMethodFilter)}</label>
            <select
              value={method}
              onChange={(e) => this.handleFilterChange('method', e.target.value)}
            >
              <option value="">{formatMessage(messages.allMethods)}</option>
              <option value="CREDIT_CARD">{formatMessage(messages.creditCard)}</option>
              <option value="ZELLE">{formatMessage(messages.zelle)}</option>
              <option value="P2C">{formatMessage(messages.p2c)}</option>
              <option value="DEBIT">{formatMessage(messages.debit)}</option>
              <option value="C2P">{formatMessage(messages.c2p)}</option>
            </select>
          </div>

          <div className={s.filterItem}>
            <label>{formatMessage(messages.transferStatusFilter)}</label>
            <select
              value={isAmountTransfer}
              onChange={(e) => this.handleFilterChange('isAmountTransfer', e.target.value)}
            >
              <option value="">{formatMessage(messages.allStatus)}</option>
              <option value="true">{formatMessage(messages.transferred)}</option>
              <option value="false">{formatMessage(messages.pending)}</option>
            </select>
          </div>


        </div>
      </div>
    );
  }

  // Render the complete payment modal
  renderCompleteModal() {
    const { showModal, bankReference, notes, modalError, submitting, selectedPayoutId } = this.state;
    const { payouts } = this.state;
    
    // Find the selected payout for display
    const selectedPayout = payouts.find(p => p.id === selectedPayoutId);

    return (
      <Modal show={showModal} onHide={this.closeModal} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: '600' }}>
            💰 Completar Pago #{selectedPayoutId}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={this.handleSubmitComplete}>
          <Modal.Body>
            {selectedPayout && (
              <div style={{ 
                backgroundColor: '#F3F4F6', 
                padding: '16px', 
                borderRadius: '8px', 
                marginBottom: '20px' 
              }}>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong>Método:</strong> {selectedPayout.method}
                </p>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong>Monto:</strong> {selectedPayout.method === 'ZELLE' 
                    ? `$${Number(selectedPayout.payoutAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                    : `Bs ${Number(selectedPayout.payoutAmount).toLocaleString('es-VE', { minimumFractionDigits: 2 })}`
                  }
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Host:</strong> {selectedPayout.listing?.user?.profile?.firstName || 'N/A'} {selectedPayout.listing?.user?.profile?.lastName || ''}
                </p>
              </div>
            )}

            {modalError && (
              <div style={{ 
                backgroundColor: '#FEE2E2', 
                color: '#991B1B', 
                padding: '12px', 
                borderRadius: '6px', 
                marginBottom: '16px',
                border: '1px solid #FECACA'
              }}>
                ⚠️ {modalError}
              </div>
            )}

            <FormGroup>
              <ControlLabel style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                Número de Referencia Bancaria <span style={{ color: '#EF4444' }}>*</span>
              </ControlLabel>
              <FormControl
                type="text"
                placeholder="Ej: 0012345678, TRX-2024-001"
                value={bankReference}
                onChange={(e) => this.setState({ bankReference: e.target.value, modalError: '' })}
                style={{ 
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  padding: '12px'
                }}
                autoFocus
                required
              />
              <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Ingresa el número de comprobante o referencia de la transferencia bancaria realizada al host.
              </p>
            </FormGroup>

            <FormGroup style={{ marginTop: '16px' }}>
              <ControlLabel style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                Notas (opcional)
              </ControlLabel>
              <FormControl
                componentClass="textarea"
                placeholder="Notas adicionales sobre este pago..."
                value={notes}
                onChange={(e) => this.setState({ notes: e.target.value })}
                rows={3}
              />
            </FormGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              onClick={this.closeModal} 
              disabled={submitting}
              style={{ marginRight: '10px' }}
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              bsStyle="primary"
              disabled={submitting || !bankReference.trim()}
              style={{
                backgroundColor: '#10B981',
                borderColor: '#10B981',
                fontWeight: '600'
              }}
            >
              {submitting ? 'Procesando...' : '✓ Confirmar Pago Completado'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }

  render() {
    const { payouts, loading, error, pagination } = this.state;
    const { formatMessage } = this.props.intl;

    if (loading) {
      return <Loader type="text" />;
    }

    if (error) {
      return (
        <div className={s.errorContainer}>
          <h3>{formatMessage(messages.errorLoadingPayouts)}</h3>
          <p>{error}</p>
        </div>
      );
    }

    return (
      <div className={cx(s.pagecontentWrapper, 'pagecontentWrapperRTL')}>
        <div>
          {this.renderFilters()}
          <CommonTable
            thead={this.thead}
            tbody={this.tbody}
            title={formatMessage(messages.payOutManagement) + ' - Megasoft (Premium)'}
          />
          {
            payouts.length > 0 &&
            <CustomPagination
              total={pagination.totalCount}
              currentPage={pagination.currentPage}
              defaultCurrent={1}
              defaultPageSize={10}
              change={this.paginationData}
              paginationLabel={formatMessage(messages.megasoftPayouts)}
            />
          }
        </div>
        
        {/* Modal for completing payments */}
        {this.renderCompleteModal()}
      </div>
    )
  }
}

const mapState = (state) => ({
  // Add any Redux state mapping if needed
});

const mapDispatch = {
  // Add any Redux actions if needed
};

export default injectIntl(
  withStyles(s)(
    connect(mapState, mapDispatch)(PayoutMegasoftManagement)
  )
); 